import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-toastify';
import api from '../api';
import Pagination from '../common/Pagination';
import Modal from '../Modal';
import ConfirmationModal from '../ConfirmationModal';
import {
  SoutenanceHeader,
  SoutenanceList,
  SoutenanceForm,
  SoutenanceDetails
} from './soutenances';
import {
  Plus,
  Edit,
  Trash2,
  GraduationCap,
  Calendar,
  MapPin,
  Clock,
  User,
  Users,
  CheckCircle,
  XCircle,
  AlertTriangle,
  Search,
  Filter
} from 'lucide-react';

const SoutenancesTab = ({ onDataChange }) => {
  // États de base
  const [soutenances, setSoutenances] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [error, setError] = useState('');
  
  // États de recherche et pagination
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [pagination, setPagination] = useState(null);

  // États pour les filtres
  const [filters, setFilters] = useState({
    statut: '',
    type_soutenance: '',
    etudiant: ''
  });

  // États pour les modals
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
  
  // États pour les données sélectionnées
  const [selectedSoutenance, setSelectedSoutenance] = useState(null);
  const [detailSoutenance, setDetailSoutenance] = useState(null);
  const [soutenanceToDelete, setSoutenanceToDelete] = useState(null);
  const [confirmConfig, setConfirmConfig] = useState({});

  // États pour le formulaire
  const [formData, setFormData] = useState({
    etudiants: [],
    titre_travail: '',
    type_soutenance: '',
    directeur_interne: '',
    directeur_externe: '',
    co_directeur_interne: '',
    co_directeur_externe: '',
    jury: '',
    date_soutenance: '',
    salle: '',
    duree_prevue: '',
    observations: ''
  });
  const [formErrors, setFormErrors] = useState({});

  // États pour les données de référence
  const [typesSoutenance, setTypesSoutenance] = useState([]);
  const [jurys, setJurys] = useState([]);
  const [salles, setSalles] = useState([]);
  const [enseignantsInternes, setEnseignantsInternes] = useState([]);
  const [enseignantsExternes, setEnseignantsExternes] = useState([]);

  // Effet pour nettoyer les états lors du démontage
  useEffect(() => {
    return () => {
      // Réinitialiser tous les états pour éviter les problèmes de navigation
      setSoutenances([]);
      setLoading(false);
      setSubmitting(false);
      setDeleteLoading(false);
      setError('');
      setSearchTerm('');
      setCurrentPage(1);
      setPageSize(10);
      setPagination(null);
      setFilters({
        statut: '',
        type_soutenance: '',
        etudiant: ''
      });
      setIsModalOpen(false);
      setIsDetailModalOpen(false);
      setIsConfirmModalOpen(false);
      setSelectedSoutenance(null);
      setDetailSoutenance(null);
      setSoutenanceToDelete(null);
      setConfirmConfig({});
      setFormData({
        etudiants: [],
        titre_travail: '',
        type_soutenance: '',
        directeur_interne: '',
        directeur_externe: '',
        co_directeur_interne: '',
        co_directeur_externe: '',
        jury: '',
        date_soutenance: '',
        salle: '',
        duree_prevue: '',
        observations: ''
      });
      setFormErrors({});
      setTypesSoutenance([]);
      setJurys([]);
      setSalles([]);
      setEnseignantsInternes([]);
      setEnseignantsExternes([]);
    };
  }, []);

  // Fonction pour récupérer les soutenances
  const fetchSoutenances = useCallback(async (page = 1, size = 10, search = '', filterParams = {}) => {
    try {
      setLoading(true);
      setError('');
      
      const params = new URLSearchParams({
        page: page.toString(),
        page_size: size.toString(),
      });

      if (search) {
        params.append('search', search);
      }

      Object.entries(filterParams).forEach(([key, value]) => {
        if (value) {
          params.append(key, value);
        }
      });

      const response = await api.get(`/soutenance/soutenances/?${params}`);
      
      if (response.data.status === 'success') {
        setSoutenances(response.data.data || []);
        setPagination(response.data.pagination || null);
        console.log(response.data.data);
      } else {
        setError(response.data.message || 'Erreur lors du chargement');
        setSoutenances([]);
        setPagination(null);
      }
    } catch (error) {
      console.error('Erreur lors du chargement des soutenances:', error);
      setError('Erreur lors du chargement des soutenances');
      setSoutenances([]);
      setPagination(null);
    } finally {
      setLoading(false);
    }
  }, []);

  // Fonction pour récupérer les données de référence
  const fetchReferenceData = useCallback(async () => {
    try {
      const [
        typesRes, jurysRes, 
        sallesRes, ensIntRes, ensExtRes
      ] = await Promise.all([
        api.get('/soutenance/types-soutenance/'),
        api.get('/soutenance/jurys/'),
        api.get('/etablissement/salles/'),
        api.get('/enseignant/enseignants/'),
        api.get('/soutenance/enseignants-externes/')
      ]);

      setTypesSoutenance(typesRes.data.data || []);
      setJurys(jurysRes.data.data || []);
      setSalles(sallesRes.data.data || []);
      setEnseignantsInternes(ensIntRes.data.data || []);
      setEnseignantsExternes(ensExtRes.data.data || []);
    } catch (error) {
      console.error('Erreur lors du chargement des données de référence:', error);
      toast.error('Erreur lors du chargement des données de référence');
    }
  }, []);

  // Effet pour charger les données initiales
  useEffect(() => {
    let isMounted = true;
    
    const loadInitialData = async () => {
      if (isMounted) {
        await fetchSoutenances(currentPage, pageSize, searchTerm, filters);
        await fetchReferenceData();
      }
    };
    
    loadInitialData();
    
    return () => {
      isMounted = false;
    };
  }, [fetchSoutenances, fetchReferenceData, currentPage, pageSize, searchTerm, filters]);

  // Gestion des changements dans le formulaire
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    if (formErrors[name]) {
      setFormErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  // Ouverture du modal avec initialisation des données
  const handleOpenModal = (soutenance = null) => {
    setSelectedSoutenance(soutenance);
    setFormErrors({});
    
    if (soutenance) {
      // Extract student IDs from different formats
      let studentIds = [];
      
      // Try to get from etudiants_soutenance relationship
      if (soutenance.etudiants_soutenance && soutenance.etudiants_soutenance.length > 0) {
        studentIds = soutenance.etudiants_soutenance
          .filter(es => es && es.etudiant)
          .map(es => es.etudiant.id);
      } 
      // Fall back to direct etudiant (legacy format)
      else if (soutenance.etudiant && soutenance.etudiant.id) {
        studentIds = [soutenance.etudiant.id];
      }

      // Format date for datetime-local input
      let formattedDate = '';
      if (soutenance.date_soutenance) {
        try {
          const date = new Date(soutenance.date_soutenance);
          if (!isNaN(date.getTime())) {
            formattedDate = date.toISOString().slice(0, 16);
          }
        } catch (e) {
          console.error('Error formatting date:', e);
        }
      }
      
      setFormData({
        etudiants: studentIds,
        titre_travail: soutenance.titre_travail || '',
        type_soutenance: soutenance.type_soutenance?.id || '',
        directeur_interne: soutenance.directeur_interne?.id || '',
        directeur_externe: soutenance.directeur_externe?.id || '',
        co_directeur_interne: soutenance.co_directeur_interne?.id || '',
        co_directeur_externe: soutenance.co_directeur_externe?.id || '',
        jury: soutenance.jury?.id || '',
        date_soutenance: formattedDate,
        salle: soutenance.salle?.id || '',
        duree_prevue: soutenance.duree_prevue || '',
        observations: soutenance.observations || ''
      });
    } else {
      setFormData({
        etudiants: [],
        titre_travail: '',
        type_soutenance: '',
        directeur_interne: '',
        directeur_externe: '',
        co_directeur_interne: '',
        co_directeur_externe: '',
        jury: '',
        date_soutenance: '',
        salle: '',
        duree_prevue: '',
        observations: ''
      });
    }
    
    setIsModalOpen(true);
  };

  // Validation du formulaire
  const validateForm = () => {
    const errors = {};
    
    if (!formData.etudiants.length) {
      errors.etudiants = 'Au moins un étudiant est requis';
    }
    
    if (!formData.titre_travail?.trim()) {
      errors.titre_travail = 'Le titre du travail est requis';
    }
    
    if (!formData.type_soutenance) {
      errors.type_soutenance = 'Le type de soutenance est requis';
    }
    
    if (!formData.jury) {
      errors.jury = 'Le jury est requis';
    }
    
    if (!formData.date_soutenance) {
      errors.date_soutenance = 'La date de soutenance est requise';
    }

    // Validate date format
    if (formData.date_soutenance) {
      try {
        const date = new Date(formData.date_soutenance);
        if (isNaN(date.getTime())) {
          errors.date_soutenance = 'Format de date invalide';
        }
      } catch (e) {
        errors.date_soutenance = 'Format de date invalide';
      }
    }
    
    if (!formData.salle) {
      errors.salle = 'La salle est requise';
    }
    
    if (!formData.duree_prevue) {
      errors.duree_prevue = 'La durée prévue est requise';
    }

    if (!formData.directeur_interne && !formData.directeur_externe) {
      errors.directeur_interne = 'Au moins un directeur (interne ou externe) est requis';
      errors.directeur_externe = 'Au moins un directeur (interne ou externe) est requis';
    }
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Soumission du formulaire
  const handleSubmit = async (e) => {
    if (e && e.preventDefault) {
      e.preventDefault();
    }
    
    if (!validateForm()) return;

    try {
      setSubmitting(true);
      
      // Vérifier si les étudiants sont déjà au bon format ou s'ils doivent être transformés
      let etudiantsFormatted = formData.etudiants;
      
      // Si les étudiants sont simplement des IDs, les formater correctement
      if (formData.etudiants.length > 0 && !formData.etudiants[0].etudiant_id) {
        etudiantsFormatted = formData.etudiants
          .filter(id => id && !isNaN(Number(id)))
          .map((etudiantId, index) => ({
            etudiant_id: Number(etudiantId),
            role: index === 0 ? 'PRINCIPAL' : 'CO_AUTEUR',
            ordre_presentation: index + 1
          }));
      }
      
      console.log('Données étudiants avant envoi:', etudiantsFormatted);
      
      // Ensure all IDs are valid numbers before sending
      const cleanedData = {
        etudiants: etudiantsFormatted,
        titre_travail: formData.titre_travail.trim(),
        type_soutenance: Number(formData.type_soutenance),
        directeur_interne: formData.directeur_interne ? Number(formData.directeur_interne) : null,
        directeur_externe: formData.directeur_externe ? Number(formData.directeur_externe) : null,
        co_directeur_interne: formData.co_directeur_interne ? Number(formData.co_directeur_interne) : null,
        co_directeur_externe: formData.co_directeur_externe ? Number(formData.co_directeur_externe) : null,
        jury: Number(formData.jury),
        date_soutenance: formData.date_soutenance,
        salle: Number(formData.salle),
        duree_prevue: parseInt(formData.duree_prevue, 10),
        observations: formData.observations || ''
      };

      if (selectedSoutenance) {
        await api.put(`/soutenance/soutenances/${selectedSoutenance.id}/`, cleanedData);
        toast.success('Soutenance modifiée avec succès');
      } else {
        await api.post('/soutenance/soutenances/', cleanedData);
        toast.success('Soutenance créée avec succès');
      }

      setIsModalOpen(false);
      await fetchSoutenances(currentPage, pageSize, searchTerm, filters);
      onDataChange && onDataChange();
    } catch (error) {
      console.error('Erreur:', error);
      if (error.response?.data?.errors) {
        setFormErrors(error.response.data.errors);
      }
      const errorMessage = error.response?.data?.message || 'Erreur lors de la sauvegarde';
      toast.error(errorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  // Affichage des détails
  const handleViewDetails = (soutenance) => {
    setDetailSoutenance(soutenance);
    setIsDetailModalOpen(true);
  };

  // Préparation de la suppression
  const handlePrepareDelete = (soutenance) => {
    setSoutenanceToDelete(soutenance);
    setConfirmConfig({
      title: 'Supprimer la soutenance',
      message: `Êtes-vous sûr de vouloir supprimer la soutenance "${soutenance.titre_travail}" ?`,
      confirmText: 'Supprimer',
      type: 'danger'
    });
    setIsConfirmModalOpen(true);
  };

  // Suppression
  const handleDelete = async () => {
    if (!soutenanceToDelete) return;

    try {
      setDeleteLoading(true);
      await api.delete(`/soutenance/soutenances/${soutenanceToDelete.id}/`);
      toast.success('Soutenance supprimée avec succès');
      
      setIsConfirmModalOpen(false);
      setSoutenanceToDelete(null);
      await fetchSoutenances(currentPage, pageSize, searchTerm, filters);
      onDataChange && onDataChange();
    } catch (error) {
      console.error('Erreur:', error);
      const errorMessage = error.response?.data?.message || 'Erreur lors de la suppression';
      toast.error(errorMessage);
    } finally {
      setDeleteLoading(false);
    }
  };

  // Gestion de la pagination
  const handlePageChange = (page) => setCurrentPage(page);
  const handlePageSizeChange = (size) => {
    setPageSize(size);
    setCurrentPage(1);
  };

  // Rendu du footer du modal
  const renderModalFooter = () => (
    <div className="flex justify-end space-x-3">
      <button
        type="button"
        onClick={() => setIsModalOpen(false)}
        className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
        disabled={submitting}
      >
        Annuler
      </button>
      <button
        type="submit"
        form="soutenance-form"
        disabled={submitting}
        className="px-6 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
      >
        {submitting && (
          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
        )}
        <span>{selectedSoutenance ? 'Modifier' : 'Créer'}</span>
      </button>
    </div>
  );

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <SoutenanceHeader
          searchTerm={searchTerm}
          onSearchChange={setSearchTerm}
          onAddNew={() => handleOpenModal()}
          filters={filters}
          onFilterChange={setFilters}
          typesSoutenance={typesSoutenance}
        />

        {/* Message d'erreur */}
        {error && (
          <div className="mb-6 bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-lg">
            {error}
          </div>
        )}

        {/* Contenu principal */}
        <div className="mt-6">
          <SoutenanceList
            soutenances={soutenances}
            loading={loading}
            searchTerm={searchTerm}
            onEdit={handleOpenModal}
            onDelete={handlePrepareDelete}
            onViewDetails={handleViewDetails}
            onAddNew={() => handleOpenModal()}
            filters={filters}
          />
        </div>

        {/* Pagination */}
        {pagination && soutenances.length > 0 && (
          <div className="mt-6">
            <Pagination
              pagination={pagination}
              onPageChange={handlePageChange}
              onPageSizeChange={handlePageSizeChange}
            />
          </div>
        )}

        {/* Modal principal */}
        <Modal
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          title={selectedSoutenance ? 'Modifier la Soutenance' : 'Nouvelle Soutenance'}
          footer={renderModalFooter()}
          maxWidth="max-w-5xl"
        >
          <SoutenanceForm
            formData={formData}
            formErrors={formErrors}
            selectedSoutenance={selectedSoutenance}
            onInputChange={handleInputChange}
            onSubmit={handleSubmit}
            submitting={submitting}
            typesSoutenance={typesSoutenance}
            jurys={jurys}
            salles={salles}
            enseignantsInternes={enseignantsInternes}
            enseignantsExternes={enseignantsExternes}
          />
        </Modal>

        {/* Modal de détails */}
        <Modal
          isOpen={isDetailModalOpen}
          onClose={() => setIsDetailModalOpen(false)}
          title="Détails de la soutenance"
          maxWidth="max-w-4xl"
        >
          <SoutenanceDetails soutenance={detailSoutenance} />
        </Modal>

        {/* Modal de confirmation */}
        <ConfirmationModal
          isOpen={isConfirmModalOpen}
          onClose={() => setIsConfirmModalOpen(false)}
          onConfirm={handleDelete}
          title={confirmConfig.title}
          message={confirmConfig.message}
          confirmText={confirmConfig.confirmText}
          cancelText="Annuler"
          type={confirmConfig.type}
          isLoading={deleteLoading}
        />
      </div>
    </div>
  );
};

export default SoutenancesTab; 